import { useCallback, useContext, useMemo } from 'react';
import { DEFAULT_RESOLUTION_SORT } from '@bo-constants/resolutions/resolutionsList';
import { FETCH_ALL_RESOLUTIONS_PER_PAGE_COUNT } from '@bo-constants/resolutions/resolutionDetails';
import { PATHS } from '@bo-constants/routes';
import type { ResolutionSort } from '@library/types/resolutions/resolutionsList';
import { ResolutionsListFiltersContext } from '@bo-hooks/resolutions/resolutionsList';
import { useGetAllResolutionsQuery } from '@bo-services/resolutionsAPI';
import { useNavigate } from 'react-router-dom';

export interface UseResolutionProps {
  resolutionSort?: ResolutionSort;
}

export const useResolutions = ({
  resolutionSort = DEFAULT_RESOLUTION_SORT
}: UseResolutionProps = {}) => {
  const navigate = useNavigate();

  const { RESOLUTIONS } = PATHS;

  const handleClickResolution = useCallback(
    ({ resolutionId }: { resolutionId: string }) => {
      navigate(`${RESOLUTIONS}/${resolutionId}`);
    },
    [navigate, RESOLUTIONS]
  );

  const {
    currentPage,
    onCurrentPageChange,
    onResetCurrentPageChange,
    resolutionListFilters
  } = useContext(ResolutionsListFiltersContext);

  const { adminUsers, createdAtFromDate, createdAtToDate, resolutionStatuses } =
    resolutionListFilters;

  const adminUsersJoined = adminUsers.join(', ');

  const statusesJoined = resolutionStatuses.join(', ');

  const sortJoined = useMemo(() => {
    if (resolutionSort.sortBy && resolutionSort.sortDirection !== 'none') {
      return `${resolutionSort.sortBy}: ${resolutionSort.sortDirection}`;
    }

    return undefined;
  }, [resolutionSort.sortBy, resolutionSort.sortDirection]);

  const {
    data: response,
    isFetching,
    isError,
    isLoading,
    isSuccess
  } = useGetAllResolutionsQuery(
    {
      adminIds: adminUsersJoined,
      fromDate: createdAtFromDate?.toISO() ?? '',
      page: currentPage,
      perPage: FETCH_ALL_RESOLUTIONS_PER_PAGE_COUNT,
      sort: sortJoined ?? '',
      statuses: statusesJoined ?? '',
      toDate: createdAtToDate?.plus({ days: 1 }).toISO() ?? ''
    },
    { refetchOnMountOrArgChange: true }
  );

  const {
    currentPage: currentPageResponse = 0,
    data: resolutions = [],
    totalPages = 0
  } = response || {};

  const isResolutionsFetching = isFetching && currentPage === 1;

  return {
    currentPage: currentPageResponse,
    isError,
    isFetching: isResolutionsFetching,
    isLoading,
    isSuccess,
    onPageChange: onCurrentPageChange,
    onResetPageChange: onResetCurrentPageChange,
    onClickResolution: handleClickResolution,
    resolutions,
    totalPages
  };
};
